import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  hämtaKunder, hämtaMoten, hämtaMote, hämtaInkopare, uppdateraMote, taBortMote, skapaPaminnelse,
  uppdateraInkopare, skapaKund, skapaInkopare, Kund, Mote, Inkopare,
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import { TEMA, useMobil } from '../theme';
import SokbarLista from '../components/SokbarLista';

// okand_mottagare sätts av mötesintaget när adressen inte kunde kopplas
// till en inköpare. Fältet finns i databasen men inte i Mote-interfacet.
function okändMottagare(m: Mote): string | null {
  return (m as any).okand_mottagare || null;
}

const FRIMEJL = ['gmail.com', 'hotmail.com', 'hotmail.se', 'outlook.com', 'live.se', 'icloud.com', 'yahoo.com', 'me.com'];
const NAMNSUFFIX = ['restaurant', 'restaurang', 'vinbar', 'bistro', 'bar', 'kok', 'kök'];

// Förslag på kundnamn från mottagarens domän: info@alliumrestaurant.se -> "Allium Restaurant".
// Bara ett utgångsläge som säljaren kan skriva om.
function gissaNamnFrånAdress(adress: string | null): string {
  if (!adress || !adress.includes('@')) return '';
  const domän = adress.split('@')[1].toLowerCase();
  if (FRIMEJL.includes(domän) || domän.startsWith('kranvinhandel')) return '';
  let ord = domän.split('.')[0].replace(/[-_]/g, ' ');
  for (const suffix of NAMNSUFFIX) {
    if (!ord.includes(' ') && ord.endsWith(suffix) && ord.length > suffix.length + 2) {
      ord = `${ord.slice(0, -suffix.length)} ${suffix}`;
      break;
    }
  }
  return ord.replace(/\b\w/g, c => c.toUpperCase());
}

// Adresser som går till hela restaurangen snarare än en person.
const ALLMÄNNA_ADRESSER = ['info', 'kontakt', 'contact', 'hej', 'hello', 'bokning', 'booking', 'order', 'bestallning',
  'ekonomi', 'faktura', 'mail', 'post', 'restaurang', 'restaurant', 'office', 'kontoret', 'admin', 'reception'];

// Förslag på inköparens namn från adressen: erik.streiby@ -> "Erik Streiby", info@ -> "Allmän adress".
function gissaPersonFrånAdress(adress: string | null): string {
  if (!adress || !adress.includes('@')) return '';
  const lokal = adress.split('@')[0].toLowerCase();
  if (ALLMÄNNA_ADRESSER.includes(lokal.replace(/[^a-zåäö]/g, ''))) return 'Allmän adress';
  const delar = lokal.split(/[._-]+/).filter(d => /^[a-zåäöéü]+$/.test(d) && d.length > 1);
  if (!delar.length) return '';
  return delar.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(' ');
}

// Adresser som är våra egna och aldrig ska sparas på en kund.
const egenAdress = (a: string) => /@(kranvinhandel\.se|kran\.vin)$/i.test(a) || a.startsWith('mote@');

type NyttProspect = {
  öppen: boolean; namn: string; stad: string;
  inkopare: string; epost: string; telefon: string;
};

// Jämförbar form av ett kundnamn: gemener, utan "AB", bara delen efter snedstreck.
function normaliseraNamn(namn: string): string {
  return (namn || '')
    .toLowerCase()
    .split('/').pop()!
    .replace(/\b(ab|aktiebolag|hb)\b/g, ' ')
    .replace(/[^\p{L}\p{N}&\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Befintliga kunder som liknar namnet i formuläret, för att undvika dubbletter.
function liknandeKunder(namn: string, kunder: Kund[]): Kund[] {
  const n = normaliseraNamn(namn);
  if (n.length < 3) return [];
  return kunder.filter(k => {
    const kort = normaliseraNamn(k.namn);
    const hel = normaliseraNamn(k.namn.replace('/', ' '));
    return kort === n || (kort.length >= 3 && (kort.includes(n) || n.includes(kort))) || hel.includes(n);
  }).slice(0, 3);
}

function inköparensId(i: Inkopare): string {
  return (i as any).inkopare_id || (i as any).inköpare_id || '';
}

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '20px 22px',
  marginBottom: '14px',
};

const monoStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
};

const labelStyle: React.CSSProperties = {
  ...monoStyle,
  fontSize: '10.5px',
  color: TEMA.inkFaint,
  display: 'block',
  marginBottom: '6px',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  fontSize: '14px',
  fontFamily: "'Inter', sans-serif",
  borderRadius: '2px',
  border: `1px solid ${TEMA.border}`,
  background: TEMA.bg,
  color: TEMA.ink,
  boxSizing: 'border-box',
  outline: 'none',
};


export default function AttKoppla() {
  const [kunder, setKunder] = useState<Kund[]>([]);
  const [moten, setMoten] = useState<Mote[]>([]);
  const [inkopare, setInkopare] = useState<Inkopare[]>([]);
  const [laddar, setLaddar] = useState(true);
  const ärMobil = useMobil();
  const [fel, setFel] = useState<string | null>(null);
  const [val, setVal] = useState<Record<string, { kund: string; inkopare: string; spara: boolean }>>({});
  // Ny inköpare med mottagarens adress, när ingen befintlig är vald. Namnet går att ändra.
  const [nyKontakt, setNyKontakt] = useState<Record<string, { spara: boolean; namn: string }>>({});
  const [arbetarId, setArbetarId] = useState<string | null>(null);
  const [nyttProspect, setNyttProspect] = useState<Record<string, NyttProspect>>({});
  const navigate = useNavigate();
  const { profil } = useAuth();

  useEffect(() => {
    Promise.all([hämtaKunder(), hämtaMoten(), hämtaInkopare()])
      .then(([k, m, i]) => {
        setKunder(k.data);
        setMoten(m.data);
        setInkopare(i.data);
        // Har mötesintaget gissat kund via domänen är den förvald, så att det
        // räcker att bekräfta med Koppla.
        setVal(Object.fromEntries(
          m.data
            .filter(x => x.kund_id && (x as any).okand_mottagare)
            .map(x => [x.mote_id, { kund: x.kund_id as string, inkopare: '', spara: true }])
        ));
        setLaddar(false);
      })
      .catch(err => {
        console.error('Kunde inte ladda okopplade aktiviteter:', err);
        setFel('Kunde inte ladda aktiviteterna just nu. Försök igen om en stund.');
        setLaddar(false);
      });
  }, []);

  // Admin ser allt, säljare ser sina egna.
  const attKoppla = moten
    .filter(m => !m.kund_id || okändMottagare(m))
    .filter(m => profil?.roll === 'admin' || m.säljare === profil?.namn)
    .sort((a, b) => (b.datum || '').localeCompare(a.datum || ''));

  const aktivaKunder = kunder.filter(k => k.aktiv).sort((a, b) => a.namn.localeCompare(b.namn, 'sv'));

  function sättVal(moteId: string, del: 'kund' | 'inkopare' | 'spara', värde: string | boolean) {
    setVal(prev => {
      const nuvarande = prev[moteId] || { kund: '', inkopare: '', spara: true };
      // Byter man kund är den tidigare inköparen inte längre relevant.
      const uppdaterat =
        del === 'kund' ? { ...nuvarande, kund: värde as string, inkopare: '' }
        : del === 'inkopare' ? { ...nuvarande, inkopare: värde as string }
        : { ...nuvarande, spara: värde as boolean };
      return { ...prev, [moteId]: uppdaterat };
    });
  }

  // Lär systemet adressen, så att nästa mejl till samma mottagare kopplas
  // automatiskt. Saknar inköparen adress blir den huvudadress, annars läggs
  // den till som en extra adress.
  async function sparaAdress(inkoparen: Inkopare, adress: string) {
    const extra = inkoparen.extra_epost || [];
    if (inkoparen.email === adress || extra.includes(adress)) return;

    const uppdaterad: Inkopare = inkoparen.email
      ? { ...inkoparen, extra_epost: [...extra, adress] }
      : { ...inkoparen, email: adress };

    const { inkopare_id, inköpare_id, ...data } = uppdaterad as any;
    await uppdateraInkopare(inköparensId(inkoparen), data);
    setInkopare(prev => prev.map(i =>
      inköparensId(i) === inköparensId(inkoparen) ? uppdaterad : i
    ));
  }

  // Sparar kopplingen: kund, eventuell inköpare, markerad som granskad.
  async function sparaKoppling(m: Mote, kundId: string, inkopareId: string | null) {
    const full = await hämtaMote(m.mote_id);
    await uppdateraMote(m.mote_id, {
      kund_id: kundId,
      inkopare_id: inkopareId,
      säljare: m.säljare,
      datum: m.datum,
      nästa_kontakt: m.nästa_kontakt || null,
      notat: m.notat,
      typ: m.typ,
      granskad: true,
      avklarad: m.avklarad,
      okand_mottagare: null,
      provningar: full.data.provningar || [],
    });
    setMoten(prev => prev.filter(x => x.mote_id !== m.mote_id));
  }

  function öppnaNyttProspect(m: Mote) {
    setNyttProspect(prev => ({
      ...prev,
      [m.mote_id]: {
        öppen: true,
        namn: gissaNamnFrånAdress(okändMottagare(m)),
        stad: '',
        inkopare: '',
        epost: (() => { const a = okändMottagare(m); return a && !a.startsWith('mote@') ? a : ''; })(),
        telefon: '',
      },
    }));
  }

  function ändraNyttProspect(moteId: string, fält: 'namn' | 'stad' | 'inkopare' | 'epost' | 'telefon', värde: string) {
    setNyttProspect(prev => ({ ...prev, [moteId]: { ...prev[moteId], [fält]: värde } }));
  }

  // Skapar kunden som prospect, och inköparen om ett namn angetts, och kopplar
  // sedan aktiviteten. Inköparen får mottagarens adress, så nästa mejl kopplas själv.
  async function skapaProspectOchKoppla(m: Mote) {
    const utkast = nyttProspect[m.mote_id];
    if (!utkast?.namn.trim()) return;
    setArbetarId(m.mote_id);
    try {
      const kundRes = await skapaKund({
        namn: utkast.namn.trim(),
        typ: 'restaurang',
        segment: null,
        stad: utkast.stad.trim(),
        matkoncept: '',
        ansvarig_säljare: m.säljare,
        aktiv: true,
        fortnox_kund_nr: null,
      } as any);
      const kundId = (kundRes.data as any).kund_id as string;

      let inkopareId: string | null = null;
      if (utkast.inkopare.trim()) {
        const inkRes = await skapaInkopare({
          kund_id: kundId,
          namn: utkast.inkopare.trim(),
          roll: '',
          smakprofil_text: '',
          föredrar_typ: '',
          prisnivå: 'medel',
          telefon: utkast.telefon.trim(),
          email: utkast.epost.trim().toLowerCase(),
        } as any);
        inkopareId = (inkRes.data as any).inkopare_id as string;
      }

      await sparaKoppling(m, kundId, inkopareId);
      const [k, i] = await Promise.all([hämtaKunder(), hämtaInkopare()]);
      setKunder(k.data);
      setInkopare(i.data);
    } catch (err) {
      console.error('Kunde inte skapa prospekten:', err);
      alert('Kunde inte skapa prospekten. Försök igen.');
    } finally {
      setArbetarId(null);
    }
  }

  async function koppla(m: Mote) {
    const valt = val[m.mote_id];
    if (!valt?.kund) return;
    setArbetarId(m.mote_id);
    try {
      const adress = okändMottagare(m);
      const vald = inkopare.find(i => inköparensId(i) === valt.inkopare);
      let inkopareId: string | null = valt.inkopare || null;
      if (valt.spara !== false && adress && !egenAdress(adress) && vald) {
        await sparaAdress(vald, adress);
      }
      // Ingen inköpare vald: spara adressen som en ny, så att nästa mejl till
      // samma person kopplas av sig självt.
      const ny = nyKontakt[m.mote_id] || { spara: true, namn: gissaPersonFrånAdress(adress) };
      if (!vald && adress && !egenAdress(adress) && ny.spara && ny.namn.trim()) {
        const finns = inkopare.find(i => i.kund_id === valt.kund
          && ((i.email || '').toLowerCase() === adress.toLowerCase() || (i.extra_epost || []).includes(adress)));
        if (finns) {
          inkopareId = inköparensId(finns);
        } else {
          const res = await skapaInkopare({
            kund_id: valt.kund, namn: ny.namn.trim(), roll: '', smakprofil_text: '', föredrar_typ: '',
            prisnivå: 'medel', telefon: '', email: adress.toLowerCase(),
          } as any);
          inkopareId = (res.data as any).inkopare_id as string;
          hämtaInkopare().then(i => setInkopare(i.data)).catch(() => {});
        }
      }

      await sparaKoppling(m, valt.kund, inkopareId);
    } catch (err) {
      console.error('Kunde inte koppla aktiviteten:', err);
      alert('Kunde inte spara. Försök igen.');
    } finally {
      setArbetarId(null);
    }
  }

  // Ibland är mejlet ingen kundaktivitet utan en lapp till en själv. Då blir
  // det en påminnelse i Att göra i stället, och aktiviteten tas bort.
  async function görTillPåminnelse(m: Mote) {
    const valt = val[m.mote_id];
    const text = (m.notat || '').trim();
    if (!text) {
      alert('Aktiviteten saknar text att göra en påminnelse av.');
      return;
    }
    setArbetarId(m.mote_id);
    try {
      await skapaPaminnelse({
        text,
        datum: m.nästa_kontakt || m.datum || new Date().toISOString().slice(0, 10),
        kund_id: valt?.kund || m.kund_id || null,
      });
      await taBortMote(m.mote_id);
      setMoten(prev => prev.filter(x => x.mote_id !== m.mote_id));
    } catch (err) {
      console.error('Kunde inte göra om till påminnelse:', err);
      alert('Kunde inte spara. Försök igen.');
    } finally {
      setArbetarId(null);
    }
  }

  async function taBort(m: Mote) {
    if (!window.confirm('Ta bort aktiviteten? Det går inte att ångra.')) return;
    setArbetarId(m.mote_id);
    try {
      await taBortMote(m.mote_id);
      setMoten(prev => prev.filter(x => x.mote_id !== m.mote_id));
    } catch (err) {
      console.error('Kunde inte ta bort aktiviteten:', err);
      alert('Kunde inte ta bort. Försök igen.');
    } finally {
      setArbetarId(null);
    }
  }

  if (laddar) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: TEMA.inkFaint, fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>Laddar...</p>
    </div>
  );

  if (fel) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: '#D9695F', fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>{fel}</p>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '30px', color: TEMA.ink }}>Att koppla</h1>
        <p style={{ margin: '10px 0 0', fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", maxWidth: '620px' }}>
          Aktiviteter som kommit in via mejl men där mottagarens adress inte finns i Vinsales.
          Välj vilken kund de hör till, så hamnar de på rätt plats.
        </p>
      </div>

      {attKoppla.length === 0 && (
        <div style={{ ...kortStyle, textAlign: 'center', padding: '40px 22px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
            Inget att koppla just nu.
          </p>
        </div>
      )}

      {attKoppla.map(m => {
        const valt = val[m.mote_id] || { kund: '', inkopare: '', spara: true };
        const kundensInkopare = inkopare.filter(i => i.kund_id === valt.kund);
        const upptagen = arbetarId === m.mote_id;
        const adress = okändMottagare(m);
        const nuvarandeKund = m.kund_id ? kunder.find(k => k.kund_id === m.kund_id) : null;

        return (
          <div key={m.mote_id} style={kortStyle}>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'baseline', flexWrap: 'wrap', marginBottom: '10px' }}>
              <span style={{ ...monoStyle, fontSize: '12px', color: TEMA.accent }}>{m.datum}</span>
              <span style={{ fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>{m.säljare}</span>
              {adress && (
                <span style={{ ...monoStyle, fontSize: '12px', color: TEMA.inkFaint }}>till {adress}</span>
              )}
              {nuvarandeKund && (
                <span style={{
                  ...monoStyle, fontSize: '11px', padding: '2px 8px', borderRadius: '2px',
                  border: `1px solid ${TEMA.border}`, color: TEMA.inkSoft,
                }}>
                  gissad kund: {nuvarandeKund.namn}
                </span>
              )}
            </div>

            <p style={{ margin: '0 0 6px', fontSize: '14.5px', color: TEMA.ink, lineHeight: 1.5 }}>
              {m.notat || <span style={{ color: TEMA.inkFaint }}>Ingen sammanfattning</span>}
            </p>
            <p style={{ margin: '0 0 16px', fontSize: '12.5px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
              {(m as any).antal_provningar > 0
                ? `Kort sammanfattning. ${(m as any).antal_provningar} provade viner med reaktioner finns på kundsidan när aktiviteten är kopplad.`
                : 'Kort sammanfattning av mejlet.'}
            </p>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px', display: 'flex', flexDirection: 'column' }}>
                <label style={labelStyle}>Kund</label>
                <SokbarLista
                  värde={valt.kund}
                  onChange={id => sättVal(m.mote_id, 'kund', id)}
                  placeholder="Sök kund..."
                  tomTillåten={false}
                  alternativ={aktivaKunder.map(k => ({
                    värde: k.kund_id,
                    etikett: k.namn,
                    under: [k.fortnox_kund_nr && `#${k.fortnox_kund_nr}`, k.stad].filter(Boolean).join(' · ') || undefined,
                  }))}
                />
              </div>

              <div style={{ flex: '1 1 220px', display: 'flex', flexDirection: 'column' }}>
                <label style={labelStyle}>Inköpare (valfritt)</label>
                <SokbarLista
                  värde={valt.inkopare}
                  onChange={id => sättVal(m.mote_id, 'inkopare', id)}
                  placeholder="Ingen inköpare"
                  disabled={!valt.kund}
                  alternativ={kundensInkopare.map(i => ({
                    värde: inköparensId(i),
                    etikett: i.namn,
                    under: i.email || undefined,
                  }))}
                />
              </div>

              <button
                onClick={() => koppla(m)}
                disabled={!valt.kund || upptagen}
                style={{
                  flexShrink: 0, background: TEMA.accent, color: TEMA.onAccent,
                  border: `1px solid ${TEMA.accent}`, borderRadius: '2px',
                  fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 500,
                  padding: '9px 18px', cursor: !valt.kund || upptagen ? 'default' : 'pointer',
                  opacity: !valt.kund || upptagen ? 0.6 : 1,
                }}
              >
                {upptagen ? 'Sparar...' : 'Koppla'}
              </button>
            </div>

            {adress && !egenAdress(adress) && valt.kund && !valt.inkopare && (() => {
              const ny = nyKontakt[m.mote_id] || { spara: true, namn: gissaPersonFrånAdress(adress) };
              const sätt = (ändring: Partial<{ spara: boolean; namn: string }>) =>
                setNyKontakt(prev => ({ ...prev, [m.mote_id]: { ...ny, ...ändring } }));
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', flexWrap: 'wrap',
                  fontFamily: "'Inter', sans-serif", fontSize: '12.5px', color: TEMA.inkSoft }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={ny.spara} onChange={e => sätt({ spara: e.target.checked })}
                      style={{ accentColor: TEMA.accent, width: '14px', height: '14px', cursor: 'pointer' }} />
                    Spara <span style={{ ...monoStyle, fontSize: '12px', color: TEMA.ink }}>{adress}</span> som ny inköpare
                  </label>
                  {ny.spara && (
                    <input value={ny.namn} onChange={e => sätt({ namn: e.target.value })} placeholder="Namn"
                      aria-label="Inköparens namn" style={{ ...inputStyle, width: '200px', padding: '5px 9px', fontSize: '13px' }} />
                  )}
                  <span style={{ color: TEMA.inkFaint }}>så kopplas nästa mejl automatiskt</span>
                </div>
              );
            })()}

            {adress && !egenAdress(adress) && valt.inkopare && (
              <label style={{
                display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px',
                fontFamily: "'Inter', sans-serif", fontSize: '12.5px', color: TEMA.inkSoft, cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={valt.spara !== false}
                  onChange={e => sättVal(m.mote_id, 'spara', e.target.checked)}
                  style={{ accentColor: TEMA.accent, width: '14px', height: '14px', cursor: 'pointer' }}
                />
                Spara <span style={{ ...monoStyle, fontSize: '12px', color: TEMA.ink }}>{adress}</span> på inköparen,
                så kopplas nästa mejl automatiskt
              </label>
            )}

            {!nyttProspect[m.mote_id]?.öppen ? (
              <button
                onClick={() => öppnaNyttProspect(m)}
                style={{
                  marginTop: '14px', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif", fontSize: '13px', color: TEMA.accent,
                }}
              >
                + Kunden finns inte – skapa prospect
              </button>
            ) : (
              <div style={{
                marginTop: '16px', padding: '16px', borderRadius: '4px',
                background: TEMA.bg, border: `1px solid ${TEMA.border}`,
              }}>
                <p style={{ ...labelStyle, color: TEMA.accent, marginBottom: '12px' }}>Nytt prospect</p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: '2 1 240px' }}>
                    <label style={labelStyle}>Kundens namn</label>
                    <input
                      value={nyttProspect[m.mote_id].namn}
                      onChange={e => ändraNyttProspect(m.mote_id, 'namn', e.target.value)}
                      placeholder="Restaurangens namn"
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ flex: '1 1 160px' }}>
                    <label style={labelStyle}>Stad</label>
                    <input
                      value={nyttProspect[m.mote_id].stad}
                      onChange={e => ändraNyttProspect(m.mote_id, 'stad', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                </div>

                {(() => {
                  const liknande = liknandeKunder(nyttProspect[m.mote_id].namn, aktivaKunder);
                  if (liknande.length === 0) return null;
                  return (
                    <div style={{
                      marginTop: '12px', padding: '12px 14px', borderRadius: '3px',
                      background: 'rgba(224,168,62,0.08)', border: '1px solid rgba(224,168,62,0.35)',
                    }}>
                      <p style={{ margin: '0 0 8px', fontSize: '12.5px', color: TEMA.accent, fontFamily: "'Inter', sans-serif" }}>
                        Det här liknar en kund som redan finns. Välj den hellre än att skapa en dubblett.
                      </p>
                      {liknande.map(k => (
                        <div key={k.kund_id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '4px 0' }}>
                          <span style={{ flex: 1, fontSize: '13.5px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>
                            {k.namn}
                            {k.fortnox_kund_nr && <span style={{ ...monoStyle, color: TEMA.inkFaint, fontSize: '12px' }}> #{k.fortnox_kund_nr}</span>}
                            {k.stad && <span style={{ color: TEMA.inkFaint }}> · {k.stad}</span>}
                          </span>
                          <button
                            onClick={() => {
                              sättVal(m.mote_id, 'kund', k.kund_id);
                              setNyttProspect(prev => ({ ...prev, [m.mote_id]: { ...prev[m.mote_id], öppen: false } }));
                            }}
                            style={{
                              background: 'transparent', border: `1px solid ${TEMA.accent}`, borderRadius: '2px',
                              padding: '5px 12px', cursor: 'pointer', flexShrink: 0,
                              fontFamily: "'Inter', sans-serif", fontSize: '12px', color: TEMA.accent,
                            }}
                          >
                            Välj den i stället
                          </button>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                <p style={{ ...labelStyle, color: TEMA.inkSoft, margin: '18px 0 10px' }}>Inköpare</p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 180px' }}>
                    <label style={labelStyle}>Namn</label>
                    <input
                      value={nyttProspect[m.mote_id].inkopare}
                      onChange={e => ändraNyttProspect(m.mote_id, 'inkopare', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ flex: '1.4 1 220px' }}>
                    <label style={labelStyle}>E-post</label>
                    <input
                      type="email"
                      value={nyttProspect[m.mote_id].epost}
                      onChange={e => ändraNyttProspect(m.mote_id, 'epost', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ flex: '1 1 150px' }}>
                    <label style={labelStyle}>Telefon</label>
                    <input
                      type="tel"
                      value={nyttProspect[m.mote_id].telefon}
                      onChange={e => ändraNyttProspect(m.mote_id, 'telefon', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                </div>
                <p style={{ margin: '10px 0 0', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                  Prospekten får {m.säljare} som ansvarig säljare.
                  {nyttProspect[m.mote_id].inkopare.trim()
                    ? ' Inköparen sparas med e-post och telefon, så kopplas nästa mejl automatiskt.'
                    : ' Fyll i inköparens namn för att spara e-post och telefon.'}
                </p>
                <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                  <button
                    onClick={() => skapaProspectOchKoppla(m)}
                    disabled={upptagen || !nyttProspect[m.mote_id].namn.trim()}
                    style={{
                      background: TEMA.accent, color: TEMA.onAccent, border: `1px solid ${TEMA.accent}`,
                      borderRadius: '2px', fontFamily: "'Inter', sans-serif", fontSize: '13px', fontWeight: 500,
                      padding: '9px 18px',
                      cursor: upptagen || !nyttProspect[m.mote_id].namn.trim() ? 'default' : 'pointer',
                      opacity: upptagen || !nyttProspect[m.mote_id].namn.trim() ? 0.6 : 1,
                    }}
                  >
                    {upptagen ? 'Sparar...' : 'Skapa och koppla'}
                  </button>
                  <button
                    onClick={() => setNyttProspect(prev => ({ ...prev, [m.mote_id]: { ...prev[m.mote_id], öppen: false } }))}
                    style={{
                      background: 'transparent', color: TEMA.inkSoft, border: `1px solid ${TEMA.border}`,
                      borderRadius: '2px', fontFamily: "'Inter', sans-serif", fontSize: '13px', padding: '9px 18px', cursor: 'pointer',
                    }}
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 18px', marginTop: '14px' }}>
              {valt.kund && (
                <button
                  onClick={() => navigate(`/kund/${valt.kund}`)}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontFamily: "'Inter', sans-serif", fontSize: '12.5px', color: TEMA.inkSoft,
                    textAlign: 'left', flex: ärMobil ? '1 1 100%' : undefined,
                  }}
                >
                  Saknas inköparen? Lägg till den på kundsidan
                </button>
              )}
              <button
                onClick={() => görTillPåminnelse(m)}
                disabled={upptagen}
                title="Flyttar texten till Att göra på startsidan och tar bort aktiviteten"
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif", fontSize: '12.5px', color: TEMA.accent,
                  marginLeft: ärMobil ? 0 : 'auto', whiteSpace: 'nowrap',
                }}
              >
                Det här är en påminnelse
              </button>
              <button
                onClick={() => taBort(m)}
                disabled={upptagen}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif", fontSize: '12.5px', color: TEMA.inkFaint,
                  whiteSpace: 'nowrap', marginLeft: ärMobil ? 'auto' : 0,
                }}
              >
                Ta bort aktiviteten
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
